import React, { forwardRef } from "react";
import { styleConstants as C } from "~/style/styleConstants";
import type { ReactNode } from "react";
import type { TextStyle } from "react-native";

export interface WithTextProps {
  /** extraSmall=12, small=14, medium=16, large=20, extraLarge=24 */
  sizeExtraSmall?: boolean;
  /** extraSmall=12, small=14, medium=16, large=20, extraLarge=24 */
  sizeSmall?: boolean;
  /** extraSmall=12, small=14, medium=16, large=20, extraLarge=24 */
  sizeMedium?: boolean;
  /** extraSmall=12, small=14, medium=16, large=20, extraLarge=24 */
  sizeLarge?: boolean;
  /** extraSmall=12, small=14, medium=16, large=20, extraLarge=24 */
  sizeExtraLarge?: boolean;

  colorDark?: boolean;
  colorDarkSoft?: boolean;
  colorDarkSofter?: boolean;
  colorLight?: boolean;
  colorLightSoft?: boolean;
  colorLightSofter?: boolean;
  colorTheme?: boolean;
  colorDanger?: boolean;
  colorSuccess?: boolean;
  colorWarning?: boolean;
  colorDisabled?: boolean;

  /** Regular=400, SemiBold=600, ExtraBold=800 */
  weightRegular?: boolean;
  /** Regular=400, SemiBold=600, ExtraBold=800 */
  weightSemiBold?: boolean;
  /** Regular=400, SemiBold=600, ExtraBold=800 */
  weightExtraBold?: boolean;

  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  alignJustify?: boolean;

  underline?: boolean;
  lineThrough?: boolean;

  children?: ReactNode;
}

export function withTextProps<InputComponent extends React.ComponentType>(
  Component: InputComponent,
): React.ComponentType<
  Omit<React.ComponentPropsWithRef<InputComponent>, keyof WithTextProps> &
    WithTextProps
> {
  type NewProps = WithTextProps & { style: any };

  return forwardRef(
    (
      {
        sizeExtraSmall,
        sizeSmall,
        sizeMedium,
        sizeLarge,
        sizeExtraLarge,

        colorDark,
        colorDarkSoft,
        colorDarkSofter,
        colorLight,
        colorLightSoft,
        colorLightSofter,
        colorTheme,
        colorDanger,
        colorSuccess,
        colorWarning,
        colorDisabled,

        weightRegular,
        weightSemiBold,
        weightExtraBold,

        alignCenter,
        alignLeft,
        alignRight,
        alignJustify,

        underline,
        lineThrough,
        style: passThroughStyle,
        ...passThroughProps
      }: NewProps,
      ref,
    ) => {
      const style: TextStyle = {};
      style.fontSize = C.fontSizeMedium;
      style.lineHeight = 24;

      if (sizeExtraSmall) {
        style.fontSize = C.fontSizeExtraSmall;
        style.lineHeight = 16;
      } else if (sizeSmall) {
        style.fontSize = C.fontSizeSmall;
        style.lineHeight = 20;
      } else if (sizeMedium) {
        style.fontSize = C.fontSizeMedium;
        style.lineHeight = 24;
      } else if (sizeLarge) {
        style.fontSize = C.fontSizeLarge;
        style.lineHeight = 26;
      } else if (sizeExtraLarge) {
        style.fontSize = C.fontSizeExtraLarge;
        style.lineHeight = 36;
      }

      style.color = C.colorTextDark;

      if (colorDark) style.color = C.colorTextDark;
      else if (colorDarkSoft) style.color = C.colorTextDarkSoft;
      else if (colorDarkSofter) style.color = C.colorTextDarkSofter;
      else if (colorLight) style.color = C.colorTextLight;
      else if (colorLightSoft) style.color = C.colorTextLightSoft;
      else if (colorLightSofter) style.color = C.colorTextLightSofter;
      else if (colorTheme) style.color = C.colorTextTheme;
      else if (colorDanger) style.color = C.colorTextDanger;
      else if (colorSuccess) style.color = C.colorTextSuccess;
      else if (colorWarning) style.color = C.colorTextWarning;
      else if (colorDisabled) style.color = C.colorTextDisabled;

      style.fontFamily = "Nunito-Regular";

      if (weightRegular) {
        style.fontFamily = "Nunito-Regular";
      } else if (weightSemiBold) {
        style.fontFamily = "Nunito-SemiBold";
      } else if (weightExtraBold) {
        style.fontFamily = "Nunito-ExtraBold";
      }

      style.textAlign = "auto";
      if (alignCenter) style.textAlign = "center";
      else if (alignLeft) style.textAlign = "left";
      else if (alignRight) style.textAlign = "right";
      else if (alignJustify) style.textAlign = "justify";

      style.textDecorationLine = "none";
      if (underline) style.textDecorationLine = "underline";
      else if (lineThrough) style.textDecorationLine = "line-through";

      return (
        <Component
          ref={ref}
          style={[style, passThroughStyle]}
          {...(passThroughProps as any)}
        />
      );
    },
  ) as any;
}
